import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { confirmDialog } from "../components/ConfirmDialog";
import {
  addChangeRequestComment,
  decideChangeApproval,
  deleteChangeAttachment,
  deleteChangeRequest,
  deleteChangeRequestComment,
  downloadAttachment,
  editChangeRequestComment,
  getChangeRequest,
  getPlaces,
  listAssignees,
  listDepartments,
  listItSystems,
  setChangeApprovers,
  updateChangeRequest,
  uploadChangeAttachment,
} from "../lib/api";
import { useApi, useMutation } from "../lib/useApi";
import { useFavorites } from "../lib/useFavorites";
import { FavoriteStar } from "../components/FavoriteStar";
import { getCurrentUser } from "../lib/auth";
import { formatBytes, formatChangeNumber, formatDate, timeAgo } from "../lib/format";
import { useChangeNumberFormat } from "../lib/changeNumbering";
import type { AttachmentRow, ChangeApprover, ChangeRequestSystem, ItSystem, UpdateChangeRequestInput } from "../lib/types";
import { Avatar } from "../components/Avatar";
import { PageBar } from "../components/Layout";
import { AttachmentThumb } from "../components/AttachmentThumb";
import { Alert, EmptyState, ErrorState, Loading, Spinner } from "../components/States";
import { RichTextEditor, RichBody, looksLikeHtml, richTextIsEmpty } from "../components/RichText";
import { ChangeConfigProvider, useChangeOptions } from "../lib/changeConfig";
import type { ChangeConfigIndex } from "../lib/changeConfig";
import type { ChangeRequestDetail } from "../lib/types";
import {
  CHANGE_APPROVAL_OPTIONS,
  dateInputToUnix,
  MultiSelectChips,
  unixToDateInput,
} from "./ChangeManagementPage";

/** Change request detail — header (number/title), an editable form body that
 *  mirrors the New Change create form, an Attachments sidebar, and a comment
 *  thread at the bottom. Modeled on the ticket detail page. */
export function ChangeRequestDetailPage() {
  return (
    <ChangeConfigProvider>
      <ChangeRequestDetailInner />
    </ChangeConfigProvider>
  );
}

function ChangeRequestDetailInner() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const cfg = useChangeOptions();
  const numFmt = useChangeNumberFormat();

  const { data, loading, error, reload } = useApi((s) => getChangeRequest(id, s), [id]);
  const assigneesApi = useApi((s) => listAssignees(s, { workType: "change_management", field: "assignee" }), []);
  const assignees = assigneesApi.data?.assignees ?? [];
  const departmentsApi = useApi((s) => listDepartments(s), []);
  const cmDepartments = (departmentsApi.data?.departments ?? []).filter((d) => d.change_management);
  const systemsApi = useApi((s) => listItSystems(undefined, s), []);
  const itSystems = systemsApi.data?.it_systems ?? [];

  const updateMut = useMutation((patch: UpdateChangeRequestInput) => updateChangeRequest(id, patch));
  const deleteMut = useMutation(() => deleteChangeRequest(id));
  const commentMut = useMutation((body: string) => addChangeRequestComment(id, body));
  const editCommentMut = useMutation((args: { commentId: string; body: string }) =>
    editChangeRequestComment(id, args.commentId, args.body),
  );
  const deleteCommentMut = useMutation((commentId: string) => deleteChangeRequestComment(id, commentId));

  const me = getCurrentUser();
  const { isFavorite, toggle: toggleFavorite } = useFavorites();
  const [comment, setComment] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editBody, setEditBody] = useState("");

  if (loading) return <Loading />;
  if (error) return <ErrorState error={error} onRetry={reload} />;
  if (!data) return <ErrorState error="not found" />;
  const r = data.request;

  async function patch(p: UpdateChangeRequestInput) {
    await updateMut.run(p);
    reload();
  }

  async function postComment(e: FormEvent) {
    e.preventDefault();
    if (richTextIsEmpty(comment)) return;
    const res = await commentMut.run(comment);
    if (res) {
      setComment("");
      reload();
    }
  }

  function startEditComment(commentId: string, body: string) {
    setEditingId(commentId);
    setEditBody(body);
  }

  async function saveEditComment() {
    if (!editingId || richTextIsEmpty(editBody)) return;
    const res = await editCommentMut.run({ commentId: editingId, body: editBody });
    if (res) {
      setEditingId(null);
      setEditBody("");
      reload();
    }
  }

  async function onDeleteComment(commentId: string) {
    const ok = await confirmDialog({
      title: "Delete comment",
      message: "Delete this comment? This cannot be undone.",
      confirmLabel: "Delete",
      danger: true,
    });
    if (!ok) return;
    const res = await deleteCommentMut.run(commentId);
    if (res) {
      if (editingId === commentId) setEditingId(null);
      reload();
    }
  }

  async function onDelete() {
    const ok = await confirmDialog({
      title: "Delete change request",
      message: `Delete change #${formatChangeNumber(r.number, numFmt)}? This cannot be undone.`,
      confirmLabel: "Delete",
      danger: true,
    });
    if (!ok) return;
    const res = await deleteMut.run();
    if (res) navigate("/change-management");
  }

  return (
    <>
      <PageBar
        crumbs={<span className="crumb-current">Change Management</span>}
        actions={
          <>
            <div className="seg" role="group" aria-label="Change view">
              <button
                type="button"
                className="seg-btn"
                onClick={() => {
                  localStorage.setItem("ac.change.view", "list");
                  navigate("/change-management");
                }}
              >
                List
              </button>
              <button
                type="button"
                className="seg-btn"
                onClick={() => {
                  localStorage.setItem("ac.change.view", "board");
                  navigate("/change-management");
                }}
              >
                Board
              </button>
            </div>
            <button className="btn primary" type="button" onClick={() => navigate("/change-management")}>
              + New Change
            </button>
          </>
        }
      />
      <div className="content">
        <Link to="/change-management" className="btn ghost small inbox-back">
          ← Back to list
        </Link>
        <div className="page-head">
          <div>
            <h1>
              <span className="mono faint" style={{ fontWeight: 600 }}>#{formatChangeNumber(r.number, numFmt)}</span>{" "}
              {r.title}
            </h1>
          </div>
          <div className="btn-row">
            <FavoriteStar on={isFavorite("change", r.id)} onToggle={() => toggleFavorite("change", r.id)} />
            <button className="btn" type="button" onClick={() => window.print()} title="Print this change request">
              🖨 Print
            </button>
            <button className="btn danger" type="button" onClick={onDelete} disabled={deleteMut.pending}>
              {deleteMut.pending ? <Spinner label="Deleting…" /> : "Delete"}
            </button>
          </div>
        </div>

        <ChangeRequestPrintSheet request={r} cfg={cfg} numFmt={numFmt} />

        {updateMut.error ? <Alert kind="error">{updateMut.error}</Alert> : null}

        <div className="detail-grid">
          <div className="detail-main">
            <ChangeForm
              request={r}
              cfg={cfg}
              assignees={assignees}
              cmDepartments={cmDepartments}
              itSystems={itSystems}
              onPatch={patch}
              pending={updateMut.pending}
              onSystemsChanged={reload}
              approversCard={
                <ChangeApprovers
                  requestId={r.id}
                  approvers={r.approvers}
                  assignees={assignees}
                  onChanged={reload}
                />
              }
            />

            {/* ----- comments (always last) ----- */}
            <div className="card">
              <div className="card-head">
                <h3 style={{ margin: 0 }}>Comments</h3>
              </div>
              <div className="card-body">
                {r.comments.length === 0 ? (
                  <EmptyState>No comments yet.</EmptyState>
                ) : (
                  <ul className="comment-list">
                    {r.comments.map((cm) => {
                      const mine = !!cm.author_id && cm.author_id === me?.id;
                      const editing = editingId === cm.id;
                      return (
                        <li key={cm.id} className="comment-row">
                          <Avatar name={cm.author_name ?? "?"} size="sm" />
                          <div className="comment-body">
                            <div className="comment-meta">
                              <span className="comment-author">{cm.author_name ?? "Unknown"}</span>
                              <span className="muted"> · {formatDate(cm.created_at)}</span>
                              {mine && !editing ? (
                                <span className="comment-actions">
                                  <button
                                    type="button"
                                    className="btn ghost small"
                                    onClick={() => startEditComment(cm.id, cm.body)}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    className="btn ghost small danger"
                                    onClick={() => onDeleteComment(cm.id)}
                                    disabled={deleteCommentMut.pending}
                                  >
                                    {deleteCommentMut.pending ? "…" : "Delete"}
                                  </button>
                                </span>
                              ) : null}
                            </div>
                            {editing ? (
                              <div style={{ marginTop: 6 }}>
                                {editCommentMut.error ? <Alert kind="error">{editCommentMut.error}</Alert> : null}
                                <RichTextEditor
                                  value={editBody}
                                  onChange={setEditBody}
                                  disabled={editCommentMut.pending}
                                  placeholder="Edit comment…"
                                />
                                <div className="btn-row" style={{ marginTop: 8 }}>
                                  <button
                                    className="btn primary small"
                                    type="button"
                                    onClick={saveEditComment}
                                    disabled={editCommentMut.pending || richTextIsEmpty(editBody)}
                                  >
                                    {editCommentMut.pending ? "Saving…" : "Save"}
                                  </button>
                                  <button
                                    className="btn ghost small"
                                    type="button"
                                    onClick={() => {
                                      setEditingId(null);
                                      setEditBody("");
                                    }}
                                    disabled={editCommentMut.pending}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div style={{ whiteSpace: "pre-wrap" }}>{cm.body}</div>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
                <form onSubmit={postComment} style={{ marginTop: 12 }}>
                  {commentMut.error ? <Alert kind="error">{commentMut.error}</Alert> : null}
                  <RichTextEditor
                    value={comment}
                    onChange={setComment}
                    disabled={commentMut.pending}
                    placeholder="Add a comment…"
                  />
                  <div className="btn-row" style={{ marginTop: 8 }}>
                    <button
                      className="btn primary"
                      type="submit"
                      disabled={commentMut.pending || richTextIsEmpty(comment)}
                    >
                      {commentMut.pending ? "Posting…" : "Comment"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <aside className="detail-side">
            <ChangeAttachments requestId={r.id} attachments={r.attachments} onChanged={reload} />
          </aside>
        </div>
      </div>
    </>
  );
}

/** Attachments card for a change request — upload (R2), download, and remove.
 *  Mirrors the work-order attachments block; gated server-side by change.write. */
function ChangeAttachments({
  requestId,
  attachments,
  onChanged,
}: {
  requestId: string;
  attachments: AttachmentRow[];
  onChanged: () => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onPick(ev: React.ChangeEvent<HTMLInputElement>) {
    const file = ev.target.files?.[0];
    ev.target.value = "";
    if (!file) return;
    setError(null);
    setUploading(true);
    try {
      await uploadChangeAttachment(requestId, file);
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Upload failed.");
    } finally {
      setUploading(false);
    }
  }

  async function onDownload(id: string, filename: string) {
    setError(null);
    try {
      await downloadAttachment(id, filename);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Download failed.");
    }
  }

  async function onRemove(attId: string, filename: string) {
    if (
      !(await confirmDialog({
        title: "Remove attachment",
        message: `Remove attachment "${filename}"? This can't be undone.`,
        danger: true,
        confirmLabel: "Remove",
      }))
    )
      return;
    setError(null);
    try {
      await deleteChangeAttachment(requestId, attId);
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Remove failed.");
    }
  }

  return (
    <div className="card">
      <div className="card-head">
        <h3 style={{ margin: 0 }}>Attachments</h3>
        <label className="btn small" style={{ cursor: uploading ? "wait" : "pointer" }}>
          {uploading ? <Spinner label="Uploading…" /> : "+ Upload"}
          <input type="file" onChange={onPick} disabled={uploading} style={{ display: "none" }} />
        </label>
      </div>
      <div className="card-body">
        {error ? <Alert kind="error">{error}</Alert> : null}
        {attachments.length === 0 ? (
          <p className="muted">No attachments yet — upload photos or docs up to 25 MB.</p>
        ) : (
          <ul className="att-list">
            {attachments.map((a) => (
              <li key={a.id} className="att-item">
                <button
                  type="button"
                  className="att-main"
                  onClick={() => onDownload(a.id, a.filename)}
                  title={`Download ${a.filename}`}
                >
                  <AttachmentThumb att={a} />
                  <span className="att-info">
                    <span className="att-name">{a.filename}</span>
                    <span className="att-sub">
                      {formatBytes(a.size_bytes)}
                      {a.created_at ? ` · ${timeAgo(a.created_at)}` : ""}
                    </span>
                  </span>
                </button>
                <button
                  type="button"
                  className="att-del"
                  onClick={() => onRemove(a.id, a.filename)}
                  title="Remove attachment"
                  aria-label={`Remove ${a.filename}`}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/** Read-only, print-optimized rendering of a change request. Hidden on screen,
 *  revealed only by the print stylesheet — reuses the shared .wo-print-sheet /
 *  .tps-* print styles (mirrors WorkOrderPrintSheet). */
function ChangeRequestPrintSheet({
  request,
  cfg,
  numFmt,
}: {
  request: ChangeRequestDetail;
  cfg: ChangeConfigIndex;
  numFmt: ReturnType<typeof useChangeNumberFormat>;
}) {
  const label = (opts: { code: string; label: string }[], code: string | null | undefined) =>
    code ? opts.find((o) => o.code === code)?.label ?? code : "—";
  const dt = (ts: number | null | undefined) => (ts ? formatDate(ts) : "—");

  const rows: [string, string][] = [
    ["Status", label(cfg.statuses, request.status)],
    ["Type", label(cfg.types, request.change_type)],
    ["Risk", label(cfg.risks, request.risk)],
    ["Urgency", label(cfg.urgencies, request.urgency)],
    ["Business impact", label(cfg.impacts, request.business_impact)],
    ["Assignee", request.assignee_name ?? "Unassigned"],
    ["Requested by", request.requested_by_name ?? "—"],
    ["Department", request.department_name ?? "—"],
    ["Target date", dt(request.target_date)],
    ["Scheduled", request.scheduled_start ? `${dt(request.scheduled_start)} → ${dt(request.scheduled_end)}` : "—"],
    ["Opened", dt(request.created_at)],
  ];
  if (request.created_by_name) rows.push(["Created by", request.created_by_name]);

  const comments = request.comments ?? [];
  const approvers = request.approvers ?? [];

  return (
    <div className="wo-print-sheet" aria-hidden="true">
      <header className="tps-head">
        <h1>
          {request.title} <span className="tps-num">#{formatChangeNumber(request.number, numFmt)}</span>
        </h1>
      </header>

      <table className="tps-props">
        <tbody>
          {rows.map(([k, v]) => (
            <tr key={k}>
              <th>{k}</th>
              <td>{v}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {request.description ? (
        <>
          <h2 className="tps-section">Description</h2>
          {looksLikeHtml(request.description) ? (
            <div className="tps-comment-body"><RichBody html={request.description} /></div>
          ) : (
            <div className="tps-comment-body" style={{ whiteSpace: "pre-wrap" }}>{request.description}</div>
          )}
        </>
      ) : null}

      {request.summary_of_change ? (
        <>
          <h2 className="tps-section">Summary of change</h2>
          <div className="tps-comment-body" style={{ whiteSpace: "pre-wrap" }}>{request.summary_of_change}</div>
        </>
      ) : null}

      {request.testing_plan ? (
        <>
          <h2 className="tps-section">Testing plan</h2>
          <div className="tps-comment-body" style={{ whiteSpace: "pre-wrap" }}>{request.testing_plan}</div>
        </>
      ) : null}

      <h2 className="tps-section">Approvers ({approvers.length})</h2>
      {approvers.length === 0 ? (
        <p>No approvers assigned.</p>
      ) : (
        <ul className="wo-print-parts">
          {approvers.map((a) => (
            <li key={a.id}>
              {a.user_name ?? "—"} <span className="tps-num">{a.status}</span>
              {a.decided_at ? ` · ${formatDate(a.decided_at)}` : ""}
            </li>
          ))}
        </ul>
      )}

      <h2 className="tps-section">Comments ({comments.length})</h2>
      {comments.length === 0 ? (
        <p>No comments.</p>
      ) : (
        <ol className="tps-comments">
          {comments.map((c) => (
            <li key={c.id} className="tps-comment">
              <div className="tps-comment-meta">
                <strong>{c.author_name ?? "System"}</strong>
                <span>{formatDate(c.created_at)}</span>
              </div>
              {looksLikeHtml(c.body) ? (
                <div className="tps-comment-body"><RichBody html={c.body} /></div>
              ) : (
                <div className="tps-comment-body" style={{ whiteSpace: "pre-wrap" }}>{c.body}</div>
              )}
            </li>
          ))}
        </ol>
      )}

      <footer className="tps-foot">Printed {formatDate(Math.floor(Date.now() / 1000))}</footer>
    </div>
  );
}

/** Approvers card — editors (change.write) add/remove approvers via a people
 *  picker and see each approver's decision pill; a user who IS an approver gets
 *  Approve/Reject buttons. Server enforces who may edit / decide. */
function ChangeApprovers({
  requestId,
  approvers,
  assignees,
  onChanged,
}: {
  requestId: string;
  approvers: ChangeApprover[];
  assignees: { id: string; name: string }[];
  onChanged: () => void;
}) {
  const meId = getCurrentUser()?.id ?? "";
  const setMut = useMutation((userIds: string[]) => setChangeApprovers(requestId, userIds));
  const decideMut = useMutation((vars: { decision: "approved" | "rejected"; note?: string }) =>
    decideChangeApproval(requestId, vars),
  );

  const currentIds = approvers.map((a) => a.user_id);
  const addable = assignees.filter((u) => !currentIds.includes(u.id));
  const myRow = approvers.find((a) => a.user_id === meId);

  async function add(userId: string) {
    const res = await setMut.run([...currentIds, userId]);
    if (res) onChanged();
  }
  async function remove(userId: string) {
    const res = await setMut.run(currentIds.filter((id) => id !== userId));
    if (res) onChanged();
  }
  async function decide(decision: "approved" | "rejected") {
    const res = await decideMut.run({ decision });
    if (res) onChanged();
  }

  function StatusPill({ status }: { status: ChangeApprover["status"] }) {
    const cls = status === "approved" ? "ok" : status === "rejected" ? "danger" : "";
    const label = status === "approved" ? "Approved" : status === "rejected" ? "Rejected" : "Pending";
    return <span className={`pill ${cls}`.trim()}>{label}</span>;
  }

  return (
    <div className="field">
      <span className="label">Approvers</span>
      {setMut.error ? <Alert kind="error">{setMut.error}</Alert> : null}
      {decideMut.error ? <Alert kind="error">{decideMut.error}</Alert> : null}

      {myRow && myRow.status === "pending" ? (
        <div className="btn-row" style={{ marginBottom: 12 }}>
          <span className="muted" style={{ alignSelf: "center" }}>Your decision:</span>
          <button className="btn small primary" onClick={() => decide("approved")} disabled={decideMut.pending}>
            Approve
          </button>
          <button className="btn small danger" onClick={() => decide("rejected")} disabled={decideMut.pending}>
            Reject
          </button>
        </div>
      ) : null}

      {approvers.length === 0 ? (
        <EmptyState>No approvers assigned.</EmptyState>
      ) : (
        <ul className="person-list change-approvers">
          {approvers.map((a) => (
            <li key={a.id} className="person-row">
              <span className="person-main">
                <Avatar name={a.user_name ?? "?"} size="sm" />
                <span className="person-name">{a.user_name ?? a.user_id}</span>
                <StatusPill status={a.status} />
              </span>
              <button
                type="button"
                className="person-remove"
                aria-label={`Remove approver ${a.user_name ?? a.user_id}`}
                onClick={() => remove(a.user_id)}
                disabled={setMut.pending}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}

      <select
        value=""
        onChange={(e) => {
          if (e.target.value) void add(e.target.value);
        }}
        aria-label="Add approver"
        disabled={setMut.pending || addable.length === 0}
        style={{ marginTop: 8 }}
      >
        <option value="">Add an approver…</option>
        {addable.map((u) => (
          <option key={u.id} value={u.id}>
            {u.name}
          </option>
        ))}
      </select>
    </div>
  );
}

/** IT Systems picker (0101) — editors add/remove the IT systems a change touches
 *  via a chip list + picker; each change persists a PATCH of system_ids and
 *  reloads. Server gates editing by change.write. Rendered inline in the form,
 *  mirroring the create form's "IT systems" field. */
function ChangeSystems({
  requestId,
  systems,
  allSystems,
  onChanged,
}: {
  requestId: string;
  systems: ChangeRequestSystem[];
  allSystems: ItSystem[];
  onChanged: () => void;
}) {
  const currentIds = systems.map((s) => s.it_system_id);
  const addable = allSystems.filter((s) => !currentIds.includes(s.id));
  const setMut = useMutation((systemIds: string[]) => updateChangeRequest(requestId, { system_ids: systemIds }));

  async function add(systemId: string) {
    const res = await setMut.run([...currentIds, systemId]);
    if (res) onChanged();
  }
  async function remove(systemId: string) {
    const res = await setMut.run(currentIds.filter((id) => id !== systemId));
    if (res) onChanged();
  }

  return (
    <div className="field">
      <span className="label">IT systems</span>
      {setMut.error ? <Alert kind="error">{setMut.error}</Alert> : null}
      {systems.length > 0 ? (
        <ul className="person-list">
          {systems.map((s) => (
            <li key={s.it_system_id} className="person-row">
              <span className="person-main">
                <span className="person-name">{s.name ?? s.it_system_id}</span>
              </span>
              <button
                type="button"
                className="person-remove"
                aria-label={`Remove ${s.name ?? s.it_system_id}`}
                onClick={() => remove(s.it_system_id)}
                disabled={setMut.pending}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <select
        value=""
        onChange={(e) => {
          if (e.target.value) void add(e.target.value);
        }}
        aria-label="Add IT system"
        disabled={setMut.pending || addable.length === 0}
      >
        <option value="">Add an IT system…</option>
        {addable.map((s) => (
          <option key={s.id} value={s.id}>
            {s.name}
          </option>
        ))}
      </select>
    </div>
  );
}

/** A labeled WYSIWYG field that edits locally and persists on blur (when the
 *  HTML changed). Stores sanitized HTML; an empty editor saves null. */
function RichField({
  label,
  value,
  onSave,
  placeholder,
  disabled,
}: {
  label: string;
  value: string | null;
  onSave: (html: string | null) => void;
  placeholder: string;
  disabled: boolean;
}) {
  const [html, setHtml] = useState(value ?? "");
  useEffect(() => setHtml(value ?? ""), [value]);
  return (
    <div
      className="field"
      onBlur={() => {
        const next = richTextIsEmpty(html) ? null : html;
        if (next !== (value ?? null)) onSave(next);
      }}
    >
      <span className="label">{label}</span>
      <RichTextEditor value={html} onChange={setHtml} placeholder={placeholder} disabled={disabled} />
    </div>
  );
}

/** The editable change-request form — a single card whose field order/grouping
 *  mirrors the New Change create form (CreateChangeModal). Every edit PATCHes
 *  immediately; free-text/WYSIWYG fields persist on blur. Status (an edit-only
 *  field with no create-form equivalent) sits at the top; a read-only
 *  "Created … by …" line footers the form. */
function ChangeForm({
  request,
  cfg,
  assignees,
  cmDepartments,
  itSystems,
  onPatch,
  pending,
  onSystemsChanged,
  approversCard,
}: {
  request: ChangeRequestDetail;
  cfg: ChangeConfigIndex;
  assignees: { id: string; name: string }[];
  cmDepartments: { id: string; name: string }[];
  itSystems: ItSystem[];
  onPatch: (p: UpdateChangeRequestInput) => void | Promise<void>;
  pending: boolean;
  onSystemsChanged: () => void;
  approversCard: React.ReactNode;
}) {
  const placesApi = useApi((s) => getPlaces(s), []);
  const contributorsApi = useApi(
    (s) => listAssignees(s, { workType: "change_management", field: "contributor" }),
    [],
  );
  const contributorOptions = (contributorsApi.data?.assignees ?? []).map((u) => ({ value: u.id, label: u.name }));
  const campusOptions = (placesApi.data?.campuses ?? []).map((o) => ({ value: o.code, label: o.label }));
  const buildingOptions = (placesApi.data?.buildings ?? [])
    .filter((b) => request.campuses_affected.length === 0 || request.campuses_affected.includes(b.campus_code))
    .map((o) => ({ value: o.code, label: o.label }));

  // Notification dates is a plain text field: edit locally, persist on blur.
  const [notifDates, setNotifDates] = useState(request.notification_dates ?? "");
  useEffect(() => setNotifDates(request.notification_dates ?? ""), [request.notification_dates]);

  const half = { flex: 1, minWidth: 0 } as const;

  return (
    <div className="card">
      <div className="card-body form-grid">
        {/* Title */}
        <label className="field">
          <span className="label">Title</span>
          <input
            type="text"
            defaultValue={request.title}
            onBlur={(e) => {
              const v = e.target.value.trim();
              if (v && v !== request.title) onPatch({ title: v });
            }}
            disabled={pending}
          />
        </label>

        {/* Description */}
        <RichField
          label="Description"
          value={request.description}
          onSave={(html) => onPatch({ description: html })}
          placeholder="Describe the change…"
          disabled={pending}
        />

        {/* Type / Risk */}
        <div className="row" style={{ gap: 12 }}>
          <label className="field" style={half}>
            <span className="label">Type</span>
            <select
              value={request.change_type ?? ""}
              onChange={(e) => onPatch({ change_type: e.target.value || null })}
              disabled={pending}
            >
              <option value="">—</option>
              {cfg.types.map((o) => (
                <option key={o.code} value={o.code}>{o.label}</option>
              ))}
            </select>
          </label>
          <label className="field" style={half}>
            <span className="label">Risk</span>
            <select value={request.risk} onChange={(e) => onPatch({ risk: e.target.value })} disabled={pending}>
              {cfg.risks.map((o) => (
                <option key={o.code} value={o.code}>{o.label}</option>
              ))}
            </select>
          </label>
        </div>

        {/* Status (edit-only field, no create-form equivalent) + Requested by */}
        <div className="row" style={{ gap: 12 }}>
          <label className="field" style={half}>
            <span className="label">Status</span>
            <select value={request.status} onChange={(e) => onPatch({ status: e.target.value })} disabled={pending}>
              {cfg.statuses.map((o) => (
                <option key={o.code} value={o.code}>{o.label}</option>
              ))}
            </select>
          </label>
          <label className="field" style={half}>
            <span className="label">Requested by</span>
            <select
              value={request.requested_by ?? ""}
              onChange={(e) => onPatch({ requested_by: e.target.value || null })}
              disabled={pending}
            >
              <option value="">—</option>
              {assignees.map((u) => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
          </label>
        </div>

        {/* Requires Approval / Target Date */}
        <div className="row" style={{ gap: 12 }}>
          <label className="field" style={half}>
            <span className="label">Requires Approval</span>
            <select
              value={request.requires_approval ?? ""}
              onChange={(e) => onPatch({ requires_approval: e.target.value || null })}
              disabled={pending}
            >
              <option value="">—</option>
              {CHANGE_APPROVAL_OPTIONS.map((o) => (
                <option key={o.code} value={o.code}>{o.label}</option>
              ))}
            </select>
          </label>
          <label className="field" style={half}>
            <span className="label">Target Date</span>
            <input
              type="date"
              value={unixToDateInput(request.target_date)}
              onChange={(e) => onPatch({ target_date: dateInputToUnix(e.target.value) })}
              disabled={pending}
            />
          </label>
        </div>

        {/* Department */}
        <label className="field">
          <span className="label">Department</span>
          <select
            value={request.department_id ?? ""}
            onChange={(e) => onPatch({ department_id: e.target.value || null })}
            disabled={pending}
          >
            <option value="">— None —</option>
            {cmDepartments.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
            {request.department_id && !cmDepartments.some((d) => d.id === request.department_id) ? (
              <option value={request.department_id}>{request.department_name ?? request.department_id}</option>
            ) : null}
          </select>
          <span className="hint">Changing this re-seeds the approvers below.</span>
        </label>

        {/* Approvers */}
        {approversCard}

        {/* Scheduled start / end */}
        <div className="row" style={{ gap: 12 }}>
          <label className="field" style={half}>
            <span className="label">Scheduled start</span>
            <input
              type="date"
              value={unixToDateInput(request.scheduled_start)}
              onChange={(e) => onPatch({ scheduled_start: dateInputToUnix(e.target.value) })}
              disabled={pending}
            />
          </label>
          <label className="field" style={half}>
            <span className="label">Scheduled end</span>
            <input
              type="date"
              value={unixToDateInput(request.scheduled_end)}
              onChange={(e) => onPatch({ scheduled_end: dateInputToUnix(e.target.value) })}
              disabled={pending}
            />
          </label>
        </div>

        {/* IT systems */}
        <ChangeSystems
          requestId={request.id}
          systems={request.systems}
          allSystems={itSystems}
          onChanged={onSystemsChanged}
        />

        {/* Urgency / Business Impact */}
        <div className="row" style={{ gap: 12 }}>
          <label className="field" style={half}>
            <span className="label">Urgency / Priority</span>
            <select value={request.urgency ?? ""} onChange={(e) => onPatch({ urgency: e.target.value || null })} disabled={pending}>
              <option value="">—</option>
              {cfg.urgencies.map((o) => (
                <option key={o.code} value={o.code}>{o.label}</option>
              ))}
            </select>
          </label>
          <label className="field" style={half}>
            <span className="label">Business Impact</span>
            <select value={request.business_impact ?? ""} onChange={(e) => onPatch({ business_impact: e.target.value || null })} disabled={pending}>
              <option value="">—</option>
              {cfg.impacts.map((o) => (
                <option key={o.code} value={o.code}>{o.label}</option>
              ))}
            </select>
          </label>
        </div>

        {/* notification / automation toggles */}
        <div className="toggle-row" style={{ marginTop: 16, marginBottom: 16 }}>
          <label className="toggle">
            <input
              type="checkbox"
              checked={request.notify_endusers}
              onChange={(e) => onPatch({ notify_endusers: e.target.checked })}
              disabled={pending}
            />
            <span>Requires notification to end users</span>
          </label>
          <label className="toggle">
            <input
              type="checkbox"
              checked={request.automated_recurring}
              onChange={(e) => onPatch({ automated_recurring: e.target.checked })}
              disabled={pending}
            />
            <span>Automated / Recurring</span>
          </label>
        </div>
        {request.notify_endusers ? (
          <label className="field">
            <span className="label">Notification Dates</span>
            <input
              type="text"
              value={notifDates}
              onChange={(e) => setNotifDates(e.target.value)}
              onBlur={() => {
                if (notifDates !== (request.notification_dates ?? "")) onPatch({ notification_dates: notifDates || null });
              }}
              placeholder="Single and/or recurring dates"
              disabled={pending}
            />
          </label>
        ) : null}
        {request.automated_recurring ? (
          <label className="field">
            <span className="label">Schedule</span>
            <select value={request.schedule ?? ""} onChange={(e) => onPatch({ schedule: e.target.value || null })} disabled={pending}>
              <option value="">—</option>
              {cfg.schedules.map((o) => (
                <option key={o.code} value={o.code}>{o.label}</option>
              ))}
            </select>
          </label>
        ) : null}

        {/* Campuses / Buildings / Contributors */}
        <MultiSelectChips
          label="Campuses Affected"
          options={campusOptions}
          selected={request.campuses_affected}
          onChange={(next) => {
            const allowed = new Set(
              (placesApi.data?.buildings ?? [])
                .filter((b) => next.length === 0 || next.includes(b.campus_code))
                .map((b) => b.code),
            );
            const buildings = request.buildings_affected.filter((c) => allowed.has(c));
            onPatch({ campuses_affected: next, buildings_affected: buildings });
          }}
          placeholder="Add a campus…"
          hint={campusOptions.length === 0 ? "No campuses configured in Settings → Facilities → Locations." : undefined}
        />
        <MultiSelectChips
          label="Buildings Affected"
          options={buildingOptions}
          selected={request.buildings_affected}
          onChange={(next) => onPatch({ buildings_affected: next })}
          placeholder="Add a building…"
        />
        <MultiSelectChips
          label="Contributors"
          options={contributorOptions}
          selected={request.contributors}
          onChange={(next) => onPatch({ contributors: next })}
          placeholder="Add a contributor…"
          hint="People who will work on the change."
        />

        {/* Summary of Change / Testing Plan */}
        <RichField
          label="Summary of Change"
          value={request.summary_of_change}
          onSave={(html) => onPatch({ summary_of_change: html })}
          placeholder="Summarize the change…"
          disabled={pending}
        />
        <RichField
          label="Testing Plan"
          value={request.testing_plan}
          onSave={(html) => onPatch({ testing_plan: html })}
          placeholder="How will this change be tested…"
          disabled={pending}
        />

        {/* Is this change budgeted? */}
        <label className="toggle" style={{ marginTop: 16, marginBottom: 16 }}>
          <input
            type="checkbox"
            checked={request.is_budgeted === "yes"}
            onChange={(e) => onPatch({ is_budgeted: e.target.checked ? "yes" : "no" })}
            disabled={pending}
          />
          <span>Is this change budgeted?</span>
        </label>

      </div>
    </div>
  );
}
